import { gql } from "@apollo/client";

export const LOAD_USERS = gql`
  query {
    getUsers {
      firstName
      lastName
      email
      id
    }
  }
`;

export const IS_LOGGED_IN = gql`
  query IsLoggedIn($role: UserRole!) {
    isLoggedIn(role: $role)
  }
`;

export const LOAD_ADMINS = gql`
  query {
    getAdmins(page: 1, limit: 20) {
      __typename
      ... on AdminSuccess {
        admins {
          firstName
          lastName
          id
          phoneNumber
          email
          role
          role_type
          images
        }
      }
      ... on BadRequest {
        message
      }
    }
  }
`;

export const LOAD_CHAT_ADMINS = gql`
  query {
    getChatAdmins {
      __typename
      ... on ChatAdminSuccess {
        users {
          id
          user {
            firstName
            lastName
            id
            phoneNumber
            email
            role
            role_type
            images
          }
        }
      }
      ... on BadRequest {
        message
      }
    }
  }
`;

export const LOAD_CARD_DETAILS = gql`
  query {
    me {
      __typename
      ... on UserQuerySuccess {
        user {
          cards {
            id
            card_name
            token
            status
            last_four_digit
            country_code
            expiry_month
            expiry_year
          }
        }
      }
    }
  }
`;

export const LOAD_PROFILE_INFO = gql`
  query {
    me {
      __typename
      ... on UserQuerySuccess {
        user {
          firstName
          lastName
          email
          id
          phoneNumber
          account_type
          images
          isVerified
          cards {
            id
          }
        }
      }
    }
  }
`;

export const REFERAL = gql`
  query {
    me {
      __typename
      ... on UserQuerySuccess {
        totalReferred
        user {
          referred
          referral_code
          referral_wallet
          referrals {
            id
            referred
            status
            reward
            createdAt
          }
        }
      }
    }
  }
`;
export const COUPONS = gql`
  query {
    me {
      __typename
      ... on UserQuerySuccess {
        user {
          coupons {
            couponId {
              id
              promotionName
              discountPercentage
              couponCode
              maxUsers
              expiresAt
              status
            }
            userId {
              firstName
            }
            usedCoupon
          }
        }
      }
    }
  }
`;

export const LOAD_DISPLAY_PICTURE = gql`
  query {
    me {
      __typename
      ... on UserQuerySuccess {
        user {
          images
        }
      }
    }
  }
`;

export const LOAD_PACKAGES = gql`
  query (
    $page: Int
    $limit: Int
    $number_travellers: Int
    $destination: String
    $departure_date: Date
    $return_date: Date
    $min_budget: Int
    $max_budget: Int
    $account_type: AccountType
    $categories: [ID]
    $visibility: PackageVisibilty
    $search: String
  ) {
    getPackages(
      page: $page
      limit: $limit
      number_travellers: $number_travellers
      destination: $destination
      departure_date: $departure_date
      return_date: $return_date
      min_budget: $min_budget
      max_budget: $max_budget
      account_type: $account_type
      categories: $categories
      visibility: $visibility
      search: $search
    ) {
      __typename
      ... on PackageSuccess {
        total
        destinations
        packages {
          id
          name
          summary
          description
          destination_address
          destination_lat: destination_long
          origin
          departure_date
          return_date
          start_date
          end_date
          booking_deadline_date
          hotel_rating
          number_adults
          number_children
          number_infants
          origin
          created_by {
            firstName
            lastName
            role
            brand {
              name
              image
            }
          }
          room_rates {
            id
            room_type
            rate
            variant_week
            package_class
          }
          variable_weeks
          visa_authorization
          minimum_percentage
          minimum_travellers
          non_refundable_amount
          country
          images
          isFavourite
          favourites
          account_type
          categories {
            id
            categoryId {
              id
              name
            }
          }
        }
      }
      ... on BadRequest {
        message
      }
    }
  }
`;

export const PROMOTION_PACKAGES = gql`
  query ($account_type: AccountType!) {
    getPromotionPackages(account_type: $account_type) {
      __typename
      ... on PromotionPackageQuerySuccess {
        packages {
          id
          package {
            id
            name
            summary
            description
            destination_address
            origin
            departure_date
            return_date
            booking_deadline_date
            hotel_rating
            number_adults
            number_children
            number_infants
            room_rates {
              id
              room_type
              rate
              variant_week
            }
            variable_weeks
            visa_authorization
            minimum_percentage
            minimum_travellers
            non_refundable_amount
            country
            images
            favourites
          }
        }
      }
    }
  }
`;

export const LOAD_PACKAGE = gql`
  query GET_PACKAGE($packageId: ID!) {
    getPackage(id: $packageId) {
      id
      name
      summary
      description
      destination_address
      destination_lat
      destination_long
      departure_date
      return_date
      vacation_duration
      booking_deadline_date
      hotel_rating
      number_adults
      number_children
      number_infants
      variable_weeks
      origin
      created_by {
        firstName
        lastName
        role
        brand {
          name
          image
          id
          user
        }
      }
      itineraries {
        id
        title
        description
        packageId
      }
      room_rates {
        id
        room_type
        rate
        variant_week
        package_class
      }
      country
      account_type
      minimum_percentage
      minimum_travellers
      non_refundable_amount
      images
      deadline_days_from_departure
      min_days_before_expiry
      vault_users {
        total
        users {
          id
          firstName
          lastName
          email
          images
        }
      }
      favourite_users {
        total
        users {
          id
          firstName
          lastName
          email
          images
        }
      }
      visibility
      inclusions
      exclusions
      start_date
      end_date
      duration_type
      deadline_days_from_departure
      min_days_before_expiry
      vacation_duration
      categories {
        categoryId {
          id
          name
        }
      }
    }
  }
`;

export const LOAD_SINGLE_PACKAGE_IMAGE = gql`
  query ($packageId: ID!) {
    getPackage(id: $packageId) {
      images
    }
  }
`;
export const LOAD_TRIPS = gql`
  query {
    me {
      __typename
      ... on UserQuerySuccess {
        user {
          favourites {
            package {
              id
              name
              summary
              description
              destination_address
              destination_lat: destination_long
              departure_date
              return_date
              booking_deadline_date
              hotel_rating
              number_adults
              number_children
              country
              images
              available
            }
          }
        }
      }
    }
  }
`;

export const LOAD_SAVINGSACCOUNT = gql`
  query {
    me {
      __typename
      ... on UserQuerySuccess {
        user {
          savingsAccounts {
            id
            name
            balance
            savings_frequency
            debit_interval
            autosave
            savings_type
            group_type
            savings_goal
            amount_to_charge
            next_debit_date
            package {
              name
              images
            }
            primary_card {
              id
              card_name
              token
              status
              last_four_digit
              country_code
            }
            target_date
            number_adults
            number_children
            number_infants
            completed
            trip_completed
            discount_fee
            createdAt
            payment_type
          }
        }
      }
    }
  }
`;
export const CUSTOM_TRIP = gql`
  query ($page: Int, $limit: Int, $status: TripStatus) {
    me(page: $page, limit: $limit, status: $status) {
      __typename
      ... on UserQuerySuccess {
        user {
          firstName
          lastName
          phoneNumber
          email
          custom_trips {
            id
            from_where
            to_where
            departure_date
            return_date
            booking_deadline_date
            booking_expiration_date
            hotel_rating
            number_adults
            number_children
            number_infants
            flight_class
            package_class
            passport_country
            itineraries {
              id
              title
              description
            }
            activities {
              id
              name
            }
            estimated_amount
            status
            inclusions
            exclusions
            receipt
            additional_info
            visa_support
            images
            favourites
            createdAt
            is_read
          }
        }
      }
    }
  }
`;

export const CUSTOM_TRIP_DETAILS = gql`
  query ($id: ID!) {
    getCustomTrip(id: $id) {
      __typename
      ... on CustomTripSuccess {
        customTrip {
          id
          from_where
          to_where
          departure_date
          return_date
          booking_deadline_date
          booking_expiration_date
          hotel_rating
          number_adults
          visa_support
          number_infants
          flight_class
          flight_support
          package_class
          passport_country
          itineraries {
            id
            title
            description
          }
          estimated_amount
          status
          receipt
          additional_info
        }
      }
      ... on BadRequest {
        message
      }
    }
  }
`;

export const LOAD_TRANSACTION_HISTORY = gql`
  query {
    me {
      __typename
      ... on UserQuerySuccess {
        user {
          transactions {
            transaction_reference
            amount
            transaction_type
            createdAt
            initiated_by
            savings_account_id {
              id
              name
              balance
            }
          }
        }
      }
    }
  }
`;

export const LOAD_PACKAGES_WISHLIST = gql`
  query {
    me {
      __typename
      ... on UserQuerySuccess {
        user {
          favourites {
            package {
              id
              name
              summary
              description
              destination_address
              destination_lat: destination_long
              created_by: departure_date
              departure_date
              return_date
              booking_deadline_date
              hotel_rating
              number_adults
              number_children
              number_infants
              room_rates {
                id
                room_type
                rate
                variant_week
              }
              country
              images
            }
          }
        }
      }
    }
  }
`;

export const LOAD_TRIP_WISHLIST = gql`
  query {
    me {
      __typename
      ... on UserQuerySuccess {
        user {
          favourites {
            trip {
              id
              name
              summary
              description
              destination_address
              destination_lat: destination_long
              created_by: departure_date
              return_date
              booking_deadline_date
              hotel_rating
              number_adults
              number_children
              room_rates {
                id
                room_type
                rate
                variant_week
              }
              country
              images
            }
          }
        }
      }
    }
  }
`;

export const LOAD_WALLET = gql`
  query {
    me {
      __typename
      ... on UserQuerySuccess {
        user {
          currentAccount {
            id
            balance
            savings_frequency
            debit_interval
            autosave
          }
        }
      }
    }
  }
`;

export const SEARCH_FOR_USER = gql`
  query ($userEmail: Email!) {
    searchThroughUsers(searchQuery: $userEmail) {
      id
      firstName
      email
    }
  }
`;

export const LOAD_GALLERY = gql`
  query {
    getGalleries {
      __typename
      ... on GalleryQuerySuccess {
        galleries {
          id
          name
          description
          images
          target_words
          country
        }
      }

      ... on BadRequest {
        message
      }
    }
  }
`;
export const GET_PACKAGE_CATEGORIES = gql`
  query {
    getCategories {
      __typename
      ... on CategorySuccess {
        categories {
          id
          name
        }
      }

      ... on BadRequest {
        message
      }
    }
  }
`;

export const GET_PROMOTION_POST = gql`
  query GetPromotion($couponCode: String) {
    getPromotionPost(couponCode: $couponCode) {
      __typename
      ... on PromotionPostQuerySuccess {
        promotionPost {
          id
          title
          partnersName
          bannerImage
          partnersIconImage
          fullBannerImage
          fullBannerBackgroundColor
          couponId {
            id
            promotionName
            couponCode
            status
            expiresAt
            discountPercentage
          }
        }
      }
      ... on BadRequest {
        message
      }
    }
  }
`;

export const LOAD_HIGHLIGHTED_PACKAGES = gql`
  query GetHighlightedPackages($account_type: AccountType) {
    getHighlightedPackages(account_type: $account_type) {
      __typename
      ... on HighlightedPackageQuerySuccess {
        packages {
          id
          package {
            id
            name
            summary
            description
            origin
            destination_address
            departure_date
            return_date
            booking_deadline_date
            number_adults
            variable_weeks
            room_rates {
              id
              room_type
              rate
              variant_week
              package_class
            }
            country
            images
            start_date
            end_date
            duration_type
            deadline_days_from_departure
            min_days_before_expiry
            vacation_duration
          }
        }
      }
      ... on BadRequest {
        message
      }
    }
  }
`;

export const LOAD_ACTIVITIES = gql`
  query {
    getActivities {
      __typename
      ... on ActivitySuccess {
        activities {
          id
          name
        }
      }

      ... on BadRequest {
        message
      }
    }
  }
`;
export const GET_FEATURED_CONTENTS = gql`
  query GetFeaturedContents {
    getFeaturedContents {
      __typename
      ... on ContentQuerySuccess {
        total
        contents {
          id
          title
          summary
          start_date
          end_date
          content_type
          contents
          content_images
          tags
          cta_link
          estimated_read_time
          createdAt
          author {
            avatar
            name
          }
          location {
            address
            type
          }
        }
      }
      ... on BadRequest {
        message
      }
    }
  }
`;

export const GET_CONTENT = gql`
  query GetContent($id: ID) {
    getContent(id: $id) {
      __typename
      ... on ContentSuccess {
        content {
          id
          title
          summary
          start_date
          end_date
          content_type
          contents
          content_images
          tags
          cta_link
          estimated_read_time
          createdAt
          author {
            avatar
            name
          }
          location {
            address
            type
          }
        }
      }
      ... on BadRequest {
        message
      }
    }
  }
`;

export const GET_ALL_CONTENTS = gql`
  query GetContents(
    $page: Int
    $limit: Int
    $sortBy: sortBy
    $date_from: Date
    $date_to: Date
    $type: ContentFilterType
    $search: String
    $eventTiming: eventTiming
  ) {
    getContents(
      page: $page
      limit: $limit
      sortBy: $sortBy
      date_from: $date_from
      date_to: $date_to
      type: $type
      search: $search
      eventTiming: $eventTiming
    ) {
      __typename
      ... on ContentQuerySuccess {
        total
        contents {
          id
          title
          summary
          start_date
          end_date
          content_type
          contents
          content_images
          tags
          cta_link
          estimated_read_time
          createdAt
          author {
            avatar
            name
          }
          location {
            address
            type
          }
        }
      }
      ... on BadRequest {
        message
      }
    }
  }
`;

export const GET_RECENT_CHATS = gql`
  query GetRecentChats($page: Int, $limit: Int) {
    getRecentChats(page: $page, limit: $limit) {
      __typename
      ... on RecentChatSuccess {
        rooms {
          room_id
          room_name
          recent_chat {
            id
            message
            message_type
            attachments
            mime_type
            createdAt
            sender {
              id
              firstName
              lastName
            }
          }
        }
      }
    }
  }
`;

export const GET_ROOM_CHATS = gql`
  query GetRoomChats($room_id: String!, $page: Int, $limit: Int) {
    getRoomChats(room_id: $room_id, page: $page, limit: $limit) {
      __typename
      ... on RoomChatSuccess {
        total
        chats {
          id
          room_id
          sender {
            id
            firstName
            lastName
          }
          receiver {
            id
            firstName
            lastName
          }
          message
          message_type
          attachments
          mime_type
          createdAt
        }
      }
    }
  }
`;

export const GET_BRAND_PACKAGES = gql`
  query GetBrandPackages($userID: ID!) {
    getBrandPackages(userID: $userID) {
      __typename
      ... on BrandPackageSuccess {
        total
        brand {
          id
          name
          image
          description
          facebook
          instagram
          twitter
          tiktok
          linkedin
          createdAt
        }
        packages {
          id
          name
          summary
          description
          origin
          images
          created_by {
            firstName
            lastName
            username
            images
            brand {
              name
              image
              user
            }
          }
          departure_date
          return_date
        }
      }
      ... on BadRequest {
        message
      }
    }
  }
`;

export const GET_PRESIGNED_URL = gql`
  query GetSignedUrl($filename: String!, $folder: String) {
    getSignedUrl(filename: $filename, folder: $folder) {
      __typename
      ... on BadRequest {
        message
      }
      ... on MediaUploadSuccess {
        uploadUrl
        objectKey
        mediaUrl
        method
        expiresIn
        headers {
          contentType
          cacheControl
        }
      }
    }
  }
`;
